import "./locationDetailsEditComponent.scss";
import * as Yup from "yup";
import React, {useCallback, useEffect, useState} from "react";
import {Field, FieldArray, FieldProps, Form, Formik, FormikHelpers} from "formik";
import Grid from "@mui/material/Grid";
import FormikAutoCompleteDropdown
    from "../../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import FormikInputComponent from "../../../../../shared/components/core/formik-input/FormikInputComponent";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../../store/reducers";
import CommonService from "../../../../../shared/services/common.service";
import {IAPIResponseType} from "../../../../../shared/models/api.model";
import {APIConfig} from "../../../../../constants";
import FromikPhoneNumberComponent
    from "../../../../../shared/components/core/fromik-phone-number/FromikPhoneNumberComponent";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {LabRoleType} from "../../../../../shared/models/lab.model";
import useFetch from "../../../../../shared/hooks/useFetch";

interface LocationDetailsEditComponentProps {
    locationInformation: any;
    onUpdateComplete: Function;
    onEditCancel: Function;
}

const emergencyContactValidationSchema = Yup.object({
    emailId: Yup.string()
        .email("Invalid Email ID")
        .nullable()
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID")
        .required("Email ID is required"),
    mobileNumber: Yup.string().nullable().required("Mobile Number is required")
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
    faxNumber: Yup.string()
        .nullable()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Fax Number should be 10 digits"),
    secondaryMobileNumber: Yup.string()
        .matches(/^[(]{0,1}[0-9]{3}[)]{0,1}[-\s]{0,1}[0-9]{3}[-\s]{0,1}[0-9]{4}$/, "Mobile Number should be 10 digits"),
});

interface emergencyContactFormSchema {
    mobileNumber: string;
    emailId: string;
    secondaryMobileNumber: string;
    faxNumber: string;
}

const emergencyContactFormInitialValues: emergencyContactFormSchema = {
    mobileNumber: "",
    emailId: "",
    secondaryMobileNumber: "",
    faxNumber: "",
}

const locationEditValidationSchema = Yup.object({
    facilityName: Yup.mixed().required("Facility Name is required"),
    name: Yup.string().required("Location Name is required"),
    locationType: Yup.mixed().required("Location Type is required"),
    labId: Yup.mixed().required("Lab Name is required"),
    addressLine1: Yup.string().nullable()
        .required("Address 1 is required")
        .max(200, 'Address Line 1 cannot exceed 200 characters'),
    addressLine2: Yup.string().nullable()
        .max(200, 'Address Line 2 cannot exceed 200 characters'),
    zipcode: Yup.mixed().required("Zip Code is required"),
    state: Yup.mixed().required("State is required"),
    city: Yup.mixed().required("City is required"),
    newZipCode: Yup.string().when('zipcode', {
        is: (zipcode: any) => (zipcode?.zipcode && zipcode?.zipcode?.toLowerCase() === "other"),
        then: Yup.string().required('Zip Code is required')
            .matches(/^[0-9]{5}$/, "Zip Code must be 5 digits"),
    }),
    emergencyContactDetails: Yup.array(emergencyContactValidationSchema).min(1),
});

interface locationEditFormSchema {
    facilityName: string;
    name: string;
    locationType: string;
    labId: string;
    addressLine1: string,
    addressLine2: string,
    zipcode: any,
    newZipCode: string,
    state: string,
    city: string,
    emergencyContactDetails: emergencyContactFormSchema[];
}

const locationEditFormInitialValues: locationEditFormSchema = {
    facilityName: "",
    name: "",
    locationType: "",
    labId: "",
    addressLine1: "",
    addressLine2: "",
    zipcode: {},
    newZipCode: "",
    state: "",
    city: "",
    emergencyContactDetails: [emergencyContactFormInitialValues],
}

const LocationDetailsEditComponent = (props: LocationDetailsEditComponentProps) => {
    const {onUpdateComplete, locationInformation, onEditCancel} = props;

    const [locationEditInitialValues, setEditLocationInitialValues] = useState<locationEditFormSchema>(locationEditFormInitialValues);

    const {facilityTypesList} = useSelector((state: IRootReducerState) => state.staticData);
    const [locationBasicInformationDetails] = useState<any>({});
    const [facilityList, setFacilityList] = useState([]);
    const [isFacilityListLoading, setIsFacilityListLoading] = useState<boolean>(false);
    const [isFacilityListLoaded, setIsFacilityListLoaded] = useState<boolean>(false);
    const [isFacilityListLoadingFailed, setIsFacilityListLoadingFailed] = useState<boolean>(false);

    const [state, setState] = useState<string>("");
    const [isZipCodeNotFound, setIsZipCodeNotFound] = useState<boolean>(false);

    const {
        response: labList = [],
        loaded: isLabListLoaded,
        loading: isLabListLoading,
        loadingFailed: isLabListLoadingFailed,
        fetchData: fetchLabList
    } = useFetch<any[]>();

    const handleZipCodeValueChange = useCallback((zipCodeObject: any, setFieldValue: any) => {
        if (zipCodeObject === "") {
            setFieldValue("state", "");
            setFieldValue("city", "");
            setIsZipCodeNotFound(false);
            setState("");
        } else {
            if (zipCodeObject?.zipcode?.toLowerCase() === "other") {
                setIsZipCodeNotFound(true);
                setFieldValue("state", "");
                setFieldValue("city", "");
            } else {
                setIsZipCodeNotFound(false);
                setFieldValue("state", zipCodeObject.state);
                setFieldValue("city", zipCodeObject.city);
            }
        }
        setFieldValue("newZipCode", "");
    }, []);

    const getFacilityList = useCallback(() => {
        const payload = {};
        setIsFacilityListLoading(true);
        setIsFacilityListLoaded(false);
        setIsFacilityListLoadingFailed(false);
        CommonService._facility.getFacilityListLite(payload)
            .then((response: IAPIResponseType<any>) => {
                setFacilityList(response.data);
                setIsFacilityListLoading(false);
                setIsFacilityListLoaded(true);
            })
            .catch((error: any) => {
                setIsFacilityListLoaded(true);
                setIsFacilityListLoadingFailed(true);
                setIsFacilityListLoading(false);
            });
    }, []);

    const getLabList = useCallback(() => {
        fetchLabList({
            url: APIConfig.FILTERED_LAB_LIST_LITE.URL,
            method: APIConfig.FILTERED_LAB_LIST_LITE.METHOD,
            payload: {"isActive": true, labRoles: ["receiveLab", "sendReceiveLab"] as LabRoleType[]}
        })
    }, [fetchLabList]);

    useEffect(() => {
        getFacilityList();
        getLabList();
    }, [getFacilityList, getLabList])

    useEffect(() => {
        if (locationInformation) {
            const locationInformationObj = {...locationInformation};
            const locationInformationDetails: any = {};
            locationInformationDetails['facilityName'] = locationInformationObj?.facilityDetails;
            locationInformationDetails['locationType'] = {
                'title': CommonService.capitalizeFirstLetter(locationInformationObj?.type),
                'code': locationInformationObj?.type
            };
            locationInformationDetails['name'] = locationInformationObj?.name;
            locationInformationDetails['labId'] = locationInformationObj?.labDetails;
            locationInformationDetails['addressLine1'] = locationInformationObj?.addressDetails?.addressLine1;
            locationInformationDetails['addressLine2'] = locationInformationObj?.addressDetails?.addressLine2;
            locationInformationDetails['emergencyContactDetails'] = locationInformationObj?.emergencyContactDetails.length > 0 ?
                locationInformationObj?.emergencyContactDetails.map((emergencyContact: any) => {
                    const obj = emergencyContact;
                    obj.mobileNumber = CommonService.getMaskedMobileNumber(obj.mobileNumber);
                    obj.secondaryMobileNumber = CommonService.getMaskedMobileNumber(obj.secondaryMobileNumber);
                    obj.faxNumber = CommonService.getMaskedMobileNumber(obj.faxNumber);
                    return obj;
                }) : [emergencyContactFormInitialValues];
            if (!locationInformationObj?.addressDetails?.zipcodeDetails) {
                locationInformationDetails['newZipCode'] = locationInformationObj?.addressDetails?.zipcode;
                locationInformationDetails['zipcode'] = {
                    zipcode: "Other"
                };
                locationInformationDetails['state'] = locationInformationObj?.addressDetails?.stateDetails;
                locationInformationDetails['city'] = locationInformationObj?.addressDetails?.cityDetails;
                setIsZipCodeNotFound(true);
            } else {
                locationInformationDetails['zipcode'] = locationInformationObj?.addressDetails?.zipcodeDetails;
                locationInformationDetails['state'] = locationInformationObj?.addressDetails?.zipcodeDetails?.state;
                locationInformationDetails['city'] = locationInformationObj?.addressDetails?.zipcodeDetails?.city;
                setIsZipCodeNotFound(false);
            }
            setState(locationInformationObj.addressDetails?.state);
            setEditLocationInitialValues(locationInformationDetails);
        }
    }, [locationInformation])

    const onLocationEditSubmit = useCallback((values: any, {
        setSubmitting,
        setErrors,
    }: FormikHelpers<any>) => {
        const locationDetails = {...values};
        if (locationDetails["newZipCode"]) {
            locationDetails["zipcode"] = locationDetails["newZipCode"];
            locationDetails["state"] = locationDetails?.state?.state;
            locationDetails["city"] = locationDetails?.city?.city;
            delete locationDetails["newZipCode"];
        } else {
            locationDetails["zipcode"] = locationDetails?.zipcode?.zipcode;
        }
        locationDetails.addressDetails = {
            'addressLine1': locationDetails.addressLine1,
            'addressLine2': locationDetails.addressLine2,
            'zipcode': locationDetails.zipcode,
            'state': locationDetails.state,
            'city': locationDetails.city,
        }
        const emergencyContactDetailsList =
            locationDetails.emergencyContactDetails.map((emergencyContact: any) => {
                return {
                    ...emergencyContact,
                    mobileNumber: emergencyContact.mobileNumber.replace(/[^0-9]/g, ""),
                    secondaryMobileNumber: emergencyContact?.secondaryMobileNumber.replace(/[^0-9]/g, ""),
                    faxNumber: emergencyContact?.faxNumber.replace(/[^0-9]/g, ""),
                };
            });

        const payload = {
            addressDetails: locationDetails.addressDetails,
            emergencyContactDetails: emergencyContactDetailsList,
            labId: locationDetails.labId.id,
            ...locationBasicInformationDetails
        };
        payload.type = locationDetails.locationType.code;
        payload.facilityId = locationDetails.facilityName.id;
        payload.name = locationDetails.name;
        setSubmitting(true);
        CommonService._location.editLocationDetails(locationInformation.id, payload)
            .then((response: IAPIResponseType<any>) => {
                setSubmitting(false);
                onUpdateComplete(response.data)
                CommonService._alert.showToast(response?.message || 'location details has been edited', "success");
            })
            .catch((error: any) => {
                CommonService.handleErrors(setErrors, error);
                setSubmitting(false);
            })
    }, [onUpdateComplete, locationBasicInformationDetails, locationInformation]);


    return (
        <div className={'location-details-edit-component details-tab-data-content'}>
            <Formik
                validationSchema={locationEditValidationSchema}
                initialValues={locationEditInitialValues}
                validateOnChange={false}
                validateOnBlur={true}
                enableReinitialize={true}
                validateOnMount={true}
                onSubmit={onLocationEditSubmit}
            >
                {({isSubmitting, dirty, values, isValid, setFieldValue, validateForm, errors}) => {
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEffect(() => {
                        validateForm();
                    }, [validateForm, values]);
                    return (
                        <Form noValidate={true} className={"location-verify-details-add-holder"}>
                            <div className="t-form-controls-wrapper">
                                <div className="form-sub-title">Basic Details</div>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'facilityName'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'Facility Name'}
                                                        required={true}
                                                        multiple={false}
                                                        placeholder={'Enter Facility Name'}
                                                        valueKey={"id"}
                                                        isDataLoaded={isFacilityListLoaded}
                                                        isDataLoadingFailed={isFacilityListLoadingFailed}
                                                        isDataLoading={isFacilityListLoading}
                                                        data={facilityList}
                                                        getOptionLabel={(option => (option.name) || '')}
                                                        formikField={field}
                                                    />
                                                )}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                        <Field name={'locationType'}>
                                            {
                                                (field: FieldProps) => (

                                                    <FormikAutoCompleteDropdown
                                                        fullWidth={true}
                                                        label={'Location Type'}
                                                        required={true}
                                                        multiple={false}
                                                        data={facilityTypesList}
                                                        valueKey={"code"}
                                                        placeholder={'Select Location Type'}
                                                        formikField={field}
                                                        getOptionLabel={(option => (option.title) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                <Field name={'name'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Location Name'}
                                                fullWidth={true}
                                                placeholder={'Enter Location Name'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )}
                                </Field>
                                <div className={'horizontal-line'}></div>
                                <div className="form-sub-title">Assigned Lab</div>
                                <Field name={'labId'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikAutoCompleteDropdown
                                                label={'Lab Name'}
                                                fullWidth={true}
                                                required={true}
                                                multiple={false}
                                                placeholder={'Enter Lab Name'}
                                                valueKey={"id"}
                                                isDataLoaded={isLabListLoaded}
                                                isDataLoadingFailed={isLabListLoadingFailed}
                                                isDataLoading={isLabListLoading}
                                                data={labList || []}
                                                getOptionLabel={(option => (option.name) || '')}
                                                formikField={field}
                                            />
                                        )}
                                </Field>
                                <div className={"horizontal-line"}/>
                                <div className="form-sub-title">Address Details</div>
                                <Field name={'addressLine1'}>
                                    {
                                        (field: FieldProps) => (
                                            <FormikInputComponent
                                                label={'Address Line 1'}
                                                placeholder={'Address Line 1*'}
                                                type={"text"}
                                                required={true}
                                                formikField={field}
                                            />
                                        )
                                    }
                                </Field>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Field name={'addressLine2'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikInputComponent
                                                        label={'Address Line 2'}
                                                        placeholder={'Address Line 2'}
                                                        type={"text"}
                                                        formikField={field}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field name={'zipcode'}>
                                            {
                                                (field: FieldProps) => (
                                                    <FormikAutoCompleteDropdown
                                                        label={'ZIP Code'}
                                                        placeholder={'Start typing for ZIP Code'}
                                                        required={true}
                                                        formikField={field}
                                                        fullWidth={true}
                                                        openOnFocus={true}
                                                        url={APIConfig.ZIPCODE_LIST.URL}
                                                        method={APIConfig.ZIPCODE_LIST.METHOD}
                                                        searchMode={'serverSide'}
                                                        data={[]}
                                                        valueKey={'zipcode'}
                                                        dataListKey={"data.docs"}
                                                        onUpdate={(value: any) => {
                                                            handleZipCodeValueChange(value, setFieldValue)
                                                        }}
                                                        //Hiding other option from zipcode
                                                        // defaultData={[
                                                        //     {
                                                        //         zipcode: "Other"
                                                        //     }
                                                        // ]}
                                                        getOptionLabel={(option => (option.zipcode) || '')}
                                                    />
                                                )
                                            }
                                        </Field>
                                    </Grid>
                                </Grid>
                                {isZipCodeNotFound && <>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'newZipCode'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            placeholder={"Enter New ZIP Code"}
                                                            label={'New ZIP Code'}
                                                            required={true}
                                                            maxLength={5}
                                                            type={"text"}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'state'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'State'}
                                                            required={true}
                                                            formikField={field}
                                                            url={APIConfig.STATE_LIST.URL}
                                                            method={APIConfig.STATE_LIST.METHOD}
                                                            searchMode={'serverSide'}
                                                            openOnFocus={true}
                                                            dataListKey={"data.docs"}
                                                            data={[]}
                                                            valueKey={'state'}
                                                            onUpdate={(value: any) => {
                                                                setState(value?.state);
                                                                field.form.setFieldValue("city", '');
                                                            }}
                                                            getOptionLabel={(option => (option.state) || '')}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field name={'city'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'City'}
                                                            placeholder={'City'}
                                                            required={true}
                                                            valueKey={'city'}
                                                            id="city_auto_complete"
                                                            formikField={field}
                                                            searchMode={'serverSide'}
                                                            url={APIConfig.CITY_LIST.URL}
                                                            method={APIConfig.CITY_LIST.METHOD}
                                                            disabled={!state}
                                                            payload={{
                                                                state: state,
                                                                page: 1,
                                                                limit: 100
                                                            }}
                                                            data={[]}
                                                            getOptionLabel={(option => (option.city) || '')}
                                                            dataListKey={"data.docs"}
                                                        />
                                                    )
                                                }
                                            </Field>

                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            {/*<Field name={'timezone'}>*/}
                                            {/*    {*/}
                                            {/*        (field: FieldProps) => (*/}
                                            {/*            <FormikAutoCompleteDropdown*/}
                                            {/*                label={'Timezone'}*/}
                                            {/*                placeholder={'Timezone'}*/}
                                            {/*                required={true}*/}
                                            {/*                id="timezone_auto_complete"*/}
                                            {/*                formikField={field}*/}
                                            {/*                searchMode={'serverSide'}*/}
                                            {/*                url={APIConfig.CITY_LIST.URL}*/}
                                            {/*                method={APIConfig.CITY_LIST.METHOD}*/}
                                            {/*                disabled={!state}*/}
                                            {/*                payload={{*/}
                                            {/*                    state: state,*/}
                                            {/*                    page: 1,*/}
                                            {/*                    limit: 100*/}
                                            {/*                }}*/}
                                            {/*                data={[]}*/}
                                            {/*                getOptionLabel={(option => (option.city) || '')}*/}
                                            {/*                dataListKey={"data.docs"}*/}
                                            {/*            />*/}
                                            {/*        )*/}
                                            {/*    }*/}
                                            {/*</Field>*/}
                                        </Grid>
                                    </Grid>
                                </>}
                                {
                                    !isZipCodeNotFound && values?.zipcode?.zipcode && values?.zipcode?.zipcode !== "Other" &&
                                    <>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'state'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'State'}
                                                                placeholder={'Enter state'}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                                type={"text"}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Field name={'city'}>
                                                    {
                                                        (field: FieldProps) => (
                                                            <FormikInputComponent
                                                                label={'City'}
                                                                placeholder={'Enter city*'}
                                                                required={true}
                                                                readOnly={true}
                                                                formikField={field}
                                                                type={"text"}
                                                            />
                                                        )
                                                    }
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    </>
                                }
                                <div className={"horizontal-line"}/>
                                <div className="form-sub-title">Emergency Contact Details</div>
                                <FieldArray name={'emergencyContactDetails'}
                                            render={(arrayHelpers) => (
                                                <>
                                                    {values?.emergencyContactDetails && values.emergencyContactDetails.map((emergencyContact: any, index: any) => {
                                                        return (
                                                            <div key={index}>
                                                                <div className="form-sub-title-wrapper">
                                                                    <div
                                                                        className="form-sub-title mrg-bottom-25 mrg-top-15">
                                                                        Contact {index + 1}:
                                                                    </div>

                                                                    {index === 1 &&
                                                                        <ButtonComponent
                                                                            className={"mrg-bottom-10 mrg-left-10"}
                                                                            variant={'outlined'}
                                                                            color={"secondary"}
                                                                            startIcon={<RemoveCircleIcon/>}
                                                                            handleClick={() => arrayHelpers.remove(index)}
                                                                        >
                                                                            Remove Contact
                                                                        </ButtonComponent>
                                                                    }
                                                                </div>
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                        <Field
                                                                            name={`emergencyContactDetails[${index}].emailId`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FormikInputComponent
                                                                                        label={'Email ID'}
                                                                                        placeholder={'Enter Email ID'}
                                                                                        type={"email"}
                                                                                        required={true}
                                                                                        formikField={field}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                        <Field
                                                                            name={`emergencyContactDetails[${index}].mobileNumber`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FromikPhoneNumberComponent
                                                                                        label={'Mobile Number'}
                                                                                        placeholder={'Mobile Number*'}
                                                                                        maxLength={14}
                                                                                        required={true}
                                                                                        formikField={field}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                        <Field
                                                                            name={`emergencyContactDetails[${index}].secondaryMobileNumber`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FromikPhoneNumberComponent
                                                                                        label={'Secondary Mobile Number'}
                                                                                        placeholder={'Secondary Mobile Number'}
                                                                                        maxLength={14}
                                                                                        formikField={field}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={12} md={6} xl={6}>
                                                                        <Field
                                                                            name={`emergencyContactDetails[${index}].faxNumber`}>
                                                                            {
                                                                                (field: FieldProps) => (
                                                                                    <FromikPhoneNumberComponent
                                                                                        label={'Fax Number'}
                                                                                        placeholder={'(000) 000-0000'}
                                                                                        maxLength={14}
                                                                                        required={false}
                                                                                        formikField={field}
                                                                                    />
                                                                                )
                                                                            }
                                                                        </Field>
                                                                    </Grid>
                                                                </Grid>

                                                                {
                                                                    index === 0 && values.emergencyContactDetails.length === 1 &&
                                                                    <div className={'add-button-with-text'}
                                                                         onClick={() => arrayHelpers.push(emergencyContactFormInitialValues)}>
                                                                        + Add Another Contact
                                                                    </div>
                                                                }
                                                            </div>
                                                        )
                                                    })}
                                                </>
                                            )
                                            }
                                />
                            </div>
                            <div className={"form-action-section-edit"}>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"secondary"}
                                    type={"button"}
                                    variant={"outlined"}
                                    disabled={isSubmitting}
                                    handleClick={() => {
                                        onEditCancel();
                                    }}
                                    fullWidth={false}
                                >
                                    Cancel
                                </ButtonComponent>
                                <ButtonComponent
                                    className={"width-25"}
                                    color={"primary"}
                                    type={"submit"}
                                    variant={"contained"}
                                    disabled={!(isValid && dirty)}
                                    fullWidth={false}
                                    isLoading={isSubmitting}
                                >
                                    Save
                                </ButtonComponent>
                            </div>
                        </Form>
                    )
                }
                }
            </Formik>
        </div>
    )
        ;

};

export default LocationDetailsEditComponent;
